import useCreateCustomFieldMutation from '@/api/mutations/custom-field-groups/useCreateCustomField';
import useCreateCustomFieldWithinFieldset from '@/api/mutations/custom-field-groups/useCreateCustomFieldWithinFieldset';
import useDeleteCustomFieldWithinFieldset from '@/api/mutations/custom-field-groups/useDeleteCustomFieldWithinFieldset';
import useOrderCustomFieldsWithinFieldset from '@/api/mutations/custom-field-groups/useOrderCustomFieldsWithinFieldset';
import useUpdateCustomFieldMutation from '@/api/mutations/custom-field-groups/useUpdateCustomField';
import useUpdateCustomFieldWithinFieldset from '@/api/mutations/custom-field-groups/useUpdateCustomFieldWithinFieldset';
import {
  CreateCustomFieldDefinitionInput,
  CreateCustomFieldWithinFieldsetInput,
  DeleteCustomFieldWithinFieldsetInput,
  OrderCustomFieldsWithinFieldsetInput,
  UpdateCustomFieldWithinFieldsetInput,
} from '@/gql/graphql';
import { FormValues } from '@/ui/components/customFieldGroupControl/CreateEditCustomFieldSidebar';
import {
  ChildFieldFormValue,
  LeafFieldFormValue,
} from '@/ui/components/customFieldGroupControl/schema';
import { isEqual, isNil, omitBy } from 'lodash-es';
import { capMultiSelectLimit } from './utils/capMultiSelectLimit';

/**
 * Builds a custom field definition input from form values.
 * Handles both leaf fields and nested fieldsets recursively.
 */
/**
 * The cap to send when updating a field. An absent limit leaves the stored cap
 * alone, so a multi-select whose box has been cleared has to say what it means:
 * every option may be chosen.
 */
function buildUpdateLimit(child: ChildFieldFormValue): number | undefined {
  if (child.type === 'upload') return child.limit;
  if (child.type === 'multi-select') {
    return (
      capMultiSelectLimit(child.limit, child.options.length) ??
      child.options.length
    );
  }
  return undefined;
}

function buildDefinitionFromFormValue(
  formValue: ChildFieldFormValue | LeafFieldFormValue
): CreateCustomFieldDefinitionInput {
  const base = {
    type: formValue.type,
    label: formValue.identification.label,
    identifier: formValue.identification.identifier,
    description: formValue.description,
  };

  if (formValue.type === 'select') {
    return {
      ...base,
      additional: {
        select: {
          options: (formValue.options || []).filter((x) => x),
          displayMode: formValue.displayMode,
        },
      },
    };
  }

  if (formValue.type === 'multi-select') {
    const options = (formValue.options || []).filter((x) => x);
    return {
      ...base,
      additional: {
        multiSelect: {
          options,
          displayMode: formValue.displayMode,
          limit: capMultiSelectLimit(formValue.limit, options.length),
        },
      },
    };
  }

  if (formValue.type === 'upload') {
    return {
      ...base,
      additional: {
        upload: {
          limit: formValue.limit || 1,
          uploadType: formValue.uploadType,
        },
      },
    };
  }

  if (formValue.type === 'fieldset') {
    return {
      ...base,
      additional: {
        fieldset: {
          children:
            formValue.children?.map((child) =>
              buildDefinitionFromFormValue(child)
            ) || [],
        },
      },
    };
  }

  // Simple types
  return {
    ...base,
    additional: {
      simple: {},
    },
  };
}

type ProcessFieldsetChildrenParams = {
  customFieldId: string;
  children: ChildFieldFormValue[];
  originalChildren: ChildFieldFormValue[] | undefined;
  subpathPrefix: string | undefined;
  createCustomFieldWithinFieldset: (
    input: CreateCustomFieldWithinFieldsetInput
  ) => Promise<unknown>;
  updateCustomFieldWithinFieldset: (
    input: UpdateCustomFieldWithinFieldsetInput
  ) => Promise<unknown>;
  deleteCustomFieldWithinFieldset: (
    input: DeleteCustomFieldWithinFieldsetInput
  ) => Promise<unknown>;
  updateOrderCustomFieldsWithinFieldset: (
    input: OrderCustomFieldsWithinFieldsetInput
  ) => Promise<unknown>;
};

/**
 * Recursively processes fieldset children for create/update/delete/reorder operations.
 * Handles both depth 1 children and nested fieldset children at depth 2.
 */
async function processFieldsetChildren(
  params: ProcessFieldsetChildrenParams
): Promise<void> {
  const {
    customFieldId,
    children,
    originalChildren,
    subpathPrefix,
    createCustomFieldWithinFieldset,
    updateCustomFieldWithinFieldset,
    deleteCustomFieldWithinFieldset,
    updateOrderCustomFieldsWithinFieldset,
  } = params;

  // Build subpath for this level (undefined for root, "parentIdentifier" for nested)
  const buildSubpath = (childIdentifier: string) =>
    subpathPrefix ? `${subpathPrefix}.${childIdentifier}` : childIdentifier;

  // Determine which children are new, existing, or deleted
  const newChildren: ChildFieldFormValue[] = [];
  const existingChildren: {
    child: ChildFieldFormValue;
    originalChild: ChildFieldFormValue;
  }[] = [];

  for (const child of children) {
    const originalChild = originalChildren?.find(
      (defaultChild) =>
        defaultChild.identification.identifier ===
        child.identification.identifier
    );

    if (originalChild) {
      existingChildren.push({ child, originalChild });
    } else {
      newChildren.push(child);
    }
  }

  const deletedChildren = originalChildren
    ? originalChildren.filter(
        (originalChild) =>
          !children.some(
            (child) =>
              child.identification.identifier ===
              originalChild.identification.identifier
          )
      )
    : [];

  // Create new children first, so that the fieldset always has at least one
  // child (the server rejects removing the last child from a fieldset)
  for (const child of newChildren) {
    await createCustomFieldWithinFieldset({
      fieldsetId: customFieldId,
      definition: buildDefinitionFromFormValue(child),
      subpath: subpathPrefix || undefined,
    });
  }

  // Delete removed children
  for (const deletedChild of deletedChildren) {
    await deleteCustomFieldWithinFieldset({
      fieldsetId: customFieldId,
      subpath: buildSubpath(deletedChild.identification.identifier),
    });
  }

  // Update existing children
  for (const { child, originalChild } of existingChildren) {
    const isTouched = !isEqual(
      omitBy(originalChild, isNil),
      omitBy(child, isNil)
    );

    if (isTouched) {
      await updateCustomFieldWithinFieldset({
        fieldsetId: customFieldId,
        subpath: buildSubpath(child.identification.identifier),
        label: child.identification.label,
        description: child.description,
        options:
          child.type === 'select' || child.type === 'multi-select'
            ? child.options
            : undefined,
        displayMode:
          child.type === 'select' || child.type === 'multi-select'
            ? child.displayMode
            : undefined,
        type: child.type,
        limit: buildUpdateLimit(child),
        uploadType: child.type === 'upload' ? child.uploadType : undefined,
      });
    }

    // Recursively handle nested fieldset children
    if (child.type === 'fieldset') {
      const nestedChild = child;
      const originalNestedChild =
        originalChild.type === 'fieldset' ? originalChild : undefined;

      await processFieldsetChildren({
        customFieldId,
        children: nestedChild.children || [],
        originalChildren: originalNestedChild?.children,
        subpathPrefix: buildSubpath(child.identification.identifier),
        createCustomFieldWithinFieldset,
        updateCustomFieldWithinFieldset,
        deleteCustomFieldWithinFieldset,
        updateOrderCustomFieldsWithinFieldset,
      });
    }
  }

  // Reorder children at this level
  await updateOrderCustomFieldsWithinFieldset({
    fieldsetId: customFieldId,
    orderedChildIdentifiers: children.map(
      (child) => child.identification.identifier
    ),
    subpath: subpathPrefix || undefined,
  });
}

const SIDEBAR_SHOWS_ERROR = { meta: { skipGlobalErrorToast: true } };

export const useUpdateCustomField = () => {
  const { mutateAsync: updateCustomField, isPending: isEditingCustomField } =
    useUpdateCustomFieldMutation(SIDEBAR_SHOWS_ERROR);

  const {
    mutateAsync: createCustomFieldWithinFieldset,
    isPending: isCreatingCustomFieldWithinFieldset,
  } = useCreateCustomFieldWithinFieldset(SIDEBAR_SHOWS_ERROR);
  const {
    mutateAsync: updateCustomFieldWithinFieldset,
    isPending: isUpdatingCustomFieldWithinFieldset,
  } = useUpdateCustomFieldWithinFieldset(SIDEBAR_SHOWS_ERROR);
  const {
    mutateAsync: deleteCustomFieldWithinFieldset,
    isPending: isDeletingCustomFieldWithinFieldset,
  } = useDeleteCustomFieldWithinFieldset(SIDEBAR_SHOWS_ERROR);
  const {
    mutateAsync: updateOrderCustomFieldsWithinFieldset,
    isPending: isUpdateOrderCustomFieldsWithinFieldset,
  } = useOrderCustomFieldsWithinFieldset(SIDEBAR_SHOWS_ERROR);

  return {
    updateCustomField: async (
      customFieldId: string,
      data: FormValues,
      originalData?: FormValues
    ) => {
      await updateCustomField({
        id: customFieldId,
        label: data.identification.label,
        description: data.description,
      });

      if (data.type === 'fieldset' && !!data.children) {
        // Recursively process fieldset children (handles both depth 1 and depth 2)
        await processFieldsetChildren({
          customFieldId,
          children: data.children,
          originalChildren: originalData?.children,
          subpathPrefix: undefined,
          createCustomFieldWithinFieldset,
          updateCustomFieldWithinFieldset,
          deleteCustomFieldWithinFieldset,
          updateOrderCustomFieldsWithinFieldset,
        });
      }
    },
    isMutating:
      isEditingCustomField ||
      isCreatingCustomFieldWithinFieldset ||
      isUpdatingCustomFieldWithinFieldset ||
      isDeletingCustomFieldWithinFieldset ||
      isUpdateOrderCustomFieldsWithinFieldset,
  };
};

export const useCreateCustomField = () => {
  const { mutateAsync: createCustomField, isPending: isCreatingCustomField } =
    useCreateCustomFieldMutation(SIDEBAR_SHOWS_ERROR);

  return {
    createCustomField: async (customFieldGroupId: string, data: FormValues) => {
      if (data.type !== 'fieldset') return;

      const additional = {
        fieldset: {
          children:
            data.children?.map((child) =>
              buildDefinitionFromFormValue(child)
            ) || [],
        },
      };

      await createCustomField({
        groupId: customFieldGroupId,
        definition: {
          type: data.type,
          label: data.identification.label,
          identifier: data.identification.identifier,
          description: data.description,
          additional,
        },
      });
    },
    isMutating: isCreatingCustomField,
  };
};
